import React, { useState } from 'react';
import { FolderOpen, Plus, Edit, Trash2, Search, Filter, Tag, Grid, List } from 'lucide-react';
import { useCategories } from '../contexts/CategoriesContext';
import './Categories.css';

const Categories = () => {
  const { 
    categories, 
    addCategory, 
    updateCategory, 
    deleteCategory 
  } = useCategories();

  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3b82f6',
    prefix: ''
  });

  const colors = [
    '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444',
    '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#64748b'
  ];

  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingCategory) {
      updateCategory(editingCategory.id, formData);
    } else {
      addCategory(formData);
    }
    resetForm();
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', color: '#3b82f6', prefix: '' });
    setEditingCategory(null);
    setShowModal(false);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description,
      color: category.color,
      prefix: category.prefix || ''
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ?')) {
      deleteCategory(id);
    }
  };

  return (
    <div className="categories fade-in">
      <div className="page-header">
        <h1 className="page-title" style={{ color: '#ffffff' }}>
          <FolderOpen size={32} />
          Gestion des Catégories
        </h1>
        <p className="page-subtitle" style={{ color: '#ffffff' }}>Organisez vos produits et services par catégories</p>
      </div>

      {/* Statistics */}
      <div className="categories-stats">
        <div className="stat-item">
          <div className="stat-icon">
            <Tag size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-number">{categories.length}</span>
            <span className="stat-label">Catégories</span>
          </div>
        </div>
        <div className="stat-item">
          <div className="stat-icon">
            <FolderOpen size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-number">{categories.reduce((sum, cat) => sum + cat.productCount, 0)}</span>
            <span className="stat-label">Produits total</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="categories-toolbar">
        <div className="search-section">
          <div className="search-input">
            <Search size={20} />
            <input
              type="text"
              placeholder="Rechercher une catégorie..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="toolbar-actions">
          <div className="view-toggle">
            <button 
              className={`view-btn ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
              title="Vue en cartes"
            >
              <Grid size={18} />
            </button>
            <button 
              className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Vue en tableau"
            >
              <List size={18} />
            </button>
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => setShowModal(true)}
          >
            <Plus size={20} />
            Nouvelle catégorie
          </button>
        </div>
      </div>

      {/* Categories Table View */}
      {viewMode === 'table' && (
        <div className="categories-table-container">
          <div className="table-header">
            <div>Couleur</div>
            <div>Nom</div>
            <div>Description</div>
            <div>Préfixe</div>
            <div>Produits</div>
            <div>Actions</div>
          </div>
          
          {filteredCategories.map(category => (
            <div key={category.id} className="table-row">
              <div className="table-cell">
                <div className="category-color" style={{ backgroundColor: category.color }}></div>
              </div>
              <div className="table-cell category-name-cell">
                <span className="category-name">{category.name}</span>
              </div>
              <div className="table-cell">
                <span className="category-description">{category.description}</span>
              </div>
              <div className="table-cell">
                <span className="prefix-badge">{category.prefix}</span>
              </div>
              <div className="table-cell">
                <span className="product-count">{category.productCount} produit{category.productCount !== 1 ? 's' : ''}</span>
              </div>
              <div className="table-cell actions">
                <button 
                  className="btn-icon"
                  onClick={() => handleEdit(category)}
                  title="Modifier"
                >
                  <Edit size={16} />
                </button>
                <button 
                  className="btn-icon delete"
                  onClick={() => handleDelete(category.id)}
                  title="Supprimer"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Categories Cards View */}
      {viewMode === 'cards' && (
        <div className="categories-grid">
          {filteredCategories.map(category => (
            <div key={category.id} className="category-card">
              <div className="category-header">
                <div 
                  className="category-color" 
                  style={{ backgroundColor: category.color }}
                ></div>
                <div className="category-info">
                  <h3 className="category-name">{category.name}</h3>
                  <p className="category-description">{category.description}</p>
                  <div className="category-prefix">
                    <strong>Préfixe:</strong> <span className="prefix-badge">{category.prefix}</span>
                  </div>
                </div>
                <div className="category-actions">
                  <button 
                    className="btn-icon"
                    onClick={() => handleEdit(category)}
                    title="Modifier"
                  >
                    <Edit size={16} />
                  </button>
                  <button 
                    className="btn-icon delete"
                    onClick={() => handleDelete(category.id)}
                    title="Supprimer"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <div className="category-footer">
                <span className="product-count">
                  {category.productCount} produit{category.productCount !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {filteredCategories.length === 0 && (
        <div className="no-categories">
          <FolderOpen size={48} />
          <h3>Aucune catégorie trouvée</h3>
          <p>Essayez de modifier vos critères de recherche</p>
        </div>
      )}

      {/* Modal de création/édition */}
      {showModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}</h2>
              <button className="modal-close" onClick={resetForm}>×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="category-form">
              <div className="form-group">
                <label>Nom de la catégorie *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Ex: Services Informatiques"
                  required
                />
              </div>

              <div className="form-group">
                <label>Préfixe d'article *</label>
                <input
                  type="text"
                  value={formData.prefix}
                  onChange={(e) => setFormData({...formData, prefix: e.target.value.toUpperCase()})}
                  placeholder="Ex: SRV, MAT, FOR"
                  maxLength="5"
                  style={{ textTransform: 'uppercase' }}
                  required
                />
                <small>Ce préfixe sera utilisé pour générer les codes d'articles automatiquement (ex: SRV-01, MAT-02)</small>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Description de la catégorie..."
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Couleur</label>
                <div className="color-picker">
                  {colors.map(color => (
                    <div
                      key={color}
                      className={`color-option ${formData.color === color ? 'selected' : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setFormData({...formData, color})}
                    />
                  ))}
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingCategory ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories; 